import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, MoreVertical, DollarSign, Calendar, User, AlertTriangle, XCircle } from 'lucide-react';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button, IconButton } from '../components/Button';
import { dealsApi } from '../lib/api';
import { format } from 'date-fns';
import type { Deal, DealStage } from '../types';

// Deal stages configuration with ASI design colors
// Matches D1 schema: discovery, qualification, proposal, negotiation, won, lost
const STAGES: { id: DealStage; name: string; bgColor: string; dotColor: string }[] = [
  { id: 'discovery', name: 'Discovery', bgColor: 'bg-[var(--asi-info-light)]', dotColor: 'bg-[var(--asi-info)]' },
  { id: 'qualification', name: 'Qualification', bgColor: 'bg-[var(--asi-warning-light)]', dotColor: 'bg-[var(--asi-warning)]' },
  { id: 'proposal', name: 'Proposal', bgColor: 'bg-[#F3E8FF]', dotColor: 'bg-[#8B5CF6]' },
  { id: 'negotiation', name: 'Negotiation', bgColor: 'bg-[#FEF3C7]', dotColor: 'bg-[#D97706]' },
  { id: 'won', name: 'Won', bgColor: 'bg-[var(--asi-success-light)]', dotColor: 'bg-[var(--asi-success)]' },
];

// Format currency
const formatCurrency = (value: number | null | undefined) => {
  if (!value) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format compact currency
const formatCompactCurrency = (value: number | null | undefined) => {
  if (!value) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return formatCurrency(value);
};

interface DealCardProps {
  deal: Deal;
  onMoveStage: (dealId: string, newStage: DealStage) => void;
  stageIndex: number;
}

function DealCard({ deal, onMoveStage, stageIndex }: DealCardProps) {
  return (
    <Card
      className="cursor-pointer group animate-fade-in-up"
      style={{ animationDelay: `${stageIndex * 50}ms` }}
      hover
    >
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h4 className="font-medium text-[var(--asi-text)] line-clamp-2 pr-2">
            {deal.title}
          </h4>
          <IconButton
            icon={<MoreVertical className="w-4 h-4" />}
            label="Options"
            size="sm"
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          />
        </div>

        {deal.contact?.company && (
          <p className="text-sm text-[var(--asi-text-muted)] mb-3">
            {deal.contact.company}
          </p>
        )}

        <div className="space-y-2.5">
          {deal.value && (
            <div className="flex items-center gap-2 text-sm">
              <div className="p-1 rounded bg-[var(--asi-success-light)]">
                <DollarSign className="w-3.5 h-3.5 text-[var(--asi-success)]" />
              </div>
              <span className="font-semibold text-[var(--asi-success)]">
                {formatCurrency(deal.value)}
              </span>
            </div>
          )}

          {deal.expected_close_date && (
            <div className="flex items-center gap-2 text-sm text-[var(--asi-text-secondary)]">
              <div className="p-1 rounded bg-[var(--asi-bg-tertiary)]">
                <Calendar className="w-3.5 h-3.5" />
              </div>
              <span>{format(new Date(deal.expected_close_date), 'MMM d, yyyy')}</span>
            </div>
          )}

          {deal.contact?.name && (
            <div className="flex items-center gap-2 text-sm text-[var(--asi-text-secondary)]">
              <div className="p-1 rounded bg-[var(--asi-bg-tertiary)]">
                <User className="w-3.5 h-3.5" />
              </div>
              <span>{deal.contact.name}</span>
            </div>
          )}
        </div>

        {deal.is_stale && (
          <div className="mt-3 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-[var(--asi-warning)]" />
            <Badge variant="warning" size="sm">Stale</Badge>
          </div>
        )}

        {/* Quick stage change buttons */}
        <div className="mt-4 pt-3 border-t border-[var(--asi-bg-tertiary)] flex flex-wrap gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {STAGES.filter((s) => s.id !== deal.stage && s.id !== 'won').slice(0, 3).map((stage) => (
            <button
              key={stage.id}
              onClick={(e) => {
                e.stopPropagation();
                onMoveStage(deal.id, stage.id);
              }}
              className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg ${stage.bgColor} hover:opacity-80 transition-all`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${stage.dotColor}`} />
              {stage.name}
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}

export function Pipeline() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['deals'],
    queryFn: () => dealsApi.list({ limit: 100 }),
  });

  const updateDealMutation = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: DealStage }) =>
      dealsApi.update(id, { stage }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });

  const deals = (data?.deals || []) as Deal[];

  // Group deals by stage
  const dealsByStage = STAGES.reduce((acc, stage) => {
    acc[stage.id] = deals.filter((d) => d.stage === stage.id);
    return acc;
  }, {} as Record<DealStage, Deal[]>);

  // Calculate totals
  const stageValues = STAGES.reduce((acc, stage) => {
    acc[stage.id] = dealsByStage[stage.id].reduce((sum, d) => sum + (d.value || 0), 0);
    return acc;
  }, {} as Record<DealStage, number>);

  const lostDeals = deals.filter((d) => d.stage === 'lost');

  const handleMoveStage = (dealId: string, newStage: DealStage) => {
    updateDealMutation.mutate({ id: dealId, stage: newStage });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[var(--asi-accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--asi-text)] tracking-tight">
            Pipeline
          </h1>
          <p className="text-[var(--asi-text-secondary)] mt-1">
            Manage your deals through the sales process
          </p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />}>Add Deal</Button>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
        {STAGES.map((stage, stageIdx) => (
          <div
            key={stage.id}
            className="flex-shrink-0 w-80 animate-fade-in-up"
            style={{ animationDelay: `${stageIdx * 75}ms` }}
          >
            {/* Column Header */}
            <div className={`${stage.bgColor} rounded-t-2xl p-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className={`w-2.5 h-2.5 rounded-full ${stage.dotColor}`} />
                  <h3 className="font-semibold text-[var(--asi-text)]">{stage.name}</h3>
                  <span className="bg-white/60 px-2 py-0.5 rounded-full text-xs font-semibold text-[var(--asi-text)]">
                    {dealsByStage[stage.id].length}
                  </span>
                </div>
                <span className="text-sm font-semibold text-[var(--asi-text)]">
                  {formatCompactCurrency(stageValues[stage.id])}
                </span>
              </div>
            </div>

            {/* Column Content */}
            <div className="bg-[var(--asi-bg-secondary)] rounded-b-2xl p-3 min-h-96 space-y-3">
              {dealsByStage[stage.id].map((deal, idx) => (
                <DealCard
                  key={deal.id}
                  deal={deal}
                  onMoveStage={handleMoveStage}
                  stageIndex={idx}
                />
              ))}

              {dealsByStage[stage.id].length === 0 && (
                <div className="text-center py-12">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-[var(--asi-bg-tertiary)] flex items-center justify-center">
                    <span className={`w-3 h-3 rounded-full ${stage.dotColor} opacity-50`} />
                  </div>
                  <p className="text-sm text-[var(--asi-text-muted)]">No deals</p>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Lost column (collapsed) */}
        <div className="flex-shrink-0 w-48 animate-fade-in-up" style={{ animationDelay: `${STAGES.length * 75}ms` }}>
          <div className="bg-[var(--asi-error-light)] rounded-2xl p-4">
            <div className="flex items-center gap-2.5">
              <XCircle className="w-4 h-4 text-[var(--asi-error)]" />
              <h3 className="font-semibold text-[var(--asi-text)]">Lost</h3>
              <span className="bg-white/60 px-2 py-0.5 rounded-full text-xs font-semibold text-[var(--asi-text)]">
                {lostDeals.length}
              </span>
            </div>
            {lostDeals.length > 0 && (
              <p className="mt-2 text-sm text-[var(--asi-error)]">
                {formatCompactCurrency(lostDeals.reduce((sum, d) => sum + (d.value || 0), 0))}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
